/**
 * DaysPill — Figma component set `Pill/Days` (node 6:78, 2 variants:
 * "4d in stage" neutral, "12d in stage" overdue).
 *
 * Neutral: `bg/subtle` + `border/default` + `text/secondary`.
 * Overdue:  `status/danger/bg` + the danger foreground, no border — matching the
 *           12d instance in `Widget/InquiriesNeedingAction` (node 12:3634).
 *
 * DESIGN NOTE, recorded: that 12d instance paints its text `#b91c1c`, which is
 * `stage/lost/text` (1:38), NOT `status/danger/text` (1:52 = #da2828). The
 * design mixes the two families on this one pill. `status/danger/text` is used
 * here because the pill's background IS `status/danger/bg` and a pill should draw
 * both halves from one pair; the substitution is a 0.2 shift in hue at 4.52:1
 * versus the original pair's 3.99:1, so it is also the accessible choice. Recorded
 * as a divergence rather than silently matched.
 *
 * THE THRESHOLD IS A JUDGMENT, NOT A FIGMA VALUE — see `OVERDUE_DAYS_IN_STAGE`
 * in `domain/stages.ts` for the reasoning.
 */

import { isOverdueInStage } from '../../domain/stages';
import { cn } from '../../lib/cn';

export interface DaysPillProps {
  /** Days in the current stage, or `null` when `stage_entered_at` is absent. */
  days: number | null;
  /** Long form ("6d in stage") for roomy layouts; short ("6d") for table cells. */
  long?: boolean;
  className?: string;
}

export function DaysPill({ days, long = false, className }: DaysPillProps): React.JSX.Element {
  // `stage_entered_at` is NOT NULL in the schema, so `null` here means the API
  // omitted it or it did not parse — an unknown, which is shown as an em dash
  // rather than as "0d". Claiming a freshly-entered stage would misrepresent the
  // one signal the overdue accent is drawn from.
  if (days === null) {
    return (
      <span
        className={cn(
          'inline-flex items-center rounded-pill border border-line bg-subtle px-2.5 py-1 text-label font-medium text-fg-muted',
          className,
        )}
        title="Time in stage is unknown"
      >
        —
      </span>
    );
  }

  const overdue = isOverdueInStage(days);
  const text = long ? `${String(days)}d in stage` : `${String(days)}d`;

  return (
    <span
      className={cn(
        'tnum inline-flex items-center rounded-pill px-2.5 py-1 text-label font-medium',
        overdue
          ? 'bg-danger-bg text-danger-fg'
          : 'border border-line bg-subtle text-fg-secondary',
        className,
      )}
      title={long ? undefined : `${String(days)}d in stage`}
    >
      {text}
    </span>
  );
}
